import { Helmet } from "react-helmet-async";
import { useContext, useEffect, useRef, useState } from "react";
import { object, string, mixed, number } from "yup";
import InputMask from "react-input-mask";
// @mui
import {
  Card,
  Stack,
  Checkbox,
  Container,
  Typography,
  Box,
  TextField,
  Grid,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
// components
import { MuiFileInput } from "mui-file-input";
import { LoadingButton } from "@mui/lab";
import CarCroqui from "../components/carCroqui";

import useSurvey from "../hooks/Services/useSurvey";
import useUtils from "../hooks/Services/useUtils";

import Scrollbar from "../components/scrollbar";
import AlertSnackbar from "../components/alert/AlertSnackbar";
import { formatCEP } from "../utils/formatNumber";
import { SnackBarContext } from "../context/SnackBarContext";

export default function NewTicket() {
  const { createSurvey, isLoading, message, setMessage } = useSurvey();
  const { fetchCep, result: resultCep, isLoading: loadingCep } = useUtils();
  const [showAlert, setShowAlert] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [getPositionsChecked, setGetPositionsChecked] = useState([]);
  const [notes, setNotes] = useState("");

  const { setSnackBarMessage } = useContext(SnackBarContext);

  const placaRegExp = /^[A-Za-z]{3}-?\d{4}$|^[A-Za-z]{3}\d[A-Za-z]\d{2}$/i;
  const chassiRegExp =
    /^[A-Za-z0-9]{3,3}[A-Za-z0-9]{6,6}[A-Za-z0-9]{2,2}[A-Za-z0-9]{6,6}$/i;

  useEffect(() => {
    if (resultCep?.erro) {
      setSnackBarMessage({
        message: "Esse CEP não existe",
        severity: "error",
      });
    }
  }, [resultCep]);

  const fullSurveySchema = object().shape({
    formValues: object().shape({
      brand: string().required("A marca é necessária"),
      model: string().required("O modelo é necessária"),
      year: string().required("O ano é necessária"),
      color: string().required("A cor é necessária"),
      plate: string()
        .matches(placaRegExp, "Digite uma placa válida.")
        .nullable(true),

      chassis: string().matches(
        chassiRegExp,
        "Digite um número de chassi válido"
      ),
      deliveryCep: string().required("É necessário informar CEP"),
      deliveryNumber: number().required("É necessário informar número"),
    }),
    fotos: object().shape({
      vehiclePics: object().shape({
        vehiclePics: mixed().required("Fotos do veículo são necessárias"),
      }),
      fileDocs: object().shape({
        docVehicle: mixed().required(
          "Foto do documento do veículo é necessária"
        ),
      }),
    }),
    mode: string().notRequired(),
  });

  const handleSubmit = (formData) => {
    fullSurveySchema
      .validate(formData, { abortEarly: false })
      .then(() => createSurvey(formData))
      .catch((error) => {
        setMessage({ message: error.errors[0], show: true, status: 500 });
      });
  };

  // ========================================================================

  const handleChange = (event) => {
    const { name, value, checked } = event.target;

    if (event.target.type === "checkbox") {
      setFormValues((prevValues) => ({ ...prevValues, [name]: checked }));
    } else {
      setFormValues((prevValues) => {
        if (name === "plate" && value === "") {
          return { ...prevValues, [name]: null };
        }

        return { ...prevValues, [name]: value };
      });
    }
  };

  const handleAlert = () => {
    setShowAlert(false);
    setMessage((prev) => {
      return { ...prev, show: false };
    });
  };

  useEffect(() => {
    if (message.message !== "" && message.show) {
      setShowAlert(true);
    }
  }, [message]);

  const [fileDocs, setFileDocs] = useState({});
  const [vehiclePics, setVehiclePics] = useState({});

  const handleInputChange = (name, event) => {
    setFileDocs((prevState) => ({
      ...prevState,
      [name]: event,
    }));
  };

  const handleInputChangeVehiclePic = (name, event) => {
    setVehiclePics((prevState) => ({
      ...prevState,
      [name]: event,
    }));
  };

  useEffect(() => {
    setFormValues((prevValues) => ({
      ...prevValues,
      deliveryStreet: resultCep?.logradouro,
    }));
    setFormValues((prevValues) => ({
      ...prevValues,
      deliveryDistrict: resultCep?.bairro,
    }));
    setFormValues((prevValues) => ({
      ...prevValues,
      deliveryCity: resultCep?.localidade,
    }));
    setFormValues((prevValues) => ({
      ...prevValues,
      deliveryState: resultCep?.uf,
    }));
  }, [resultCep]);

  return (
    <>
      <Helmet>
        <title>Nova Vistoria</title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Solicitar Vistoria
          </Typography>
        </Stack>

        <Card className="p-2">
          <Scrollbar>
            <Card sx={{ boxShadow: 3 }} className="mb-4">
              <Typography variant="h6">Sobre o veículo</Typography>
              <Box className="d-flex mb-1">
                <Grid
                  container
                  rowSpacing={1}
                  columnSpacing={{ xs: 1, sm: 2, md: 3 }}
                >
                  <Grid item xs={6}>
                    <TextField
                      id="outlined-basic"
                      label="Marca"
                      variant="outlined"
                      className="w-100"
                      name="brand"
                      value={formValues.brand || ""}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      id="outlined-basic"
                      label="Modelo"
                      variant="outlined"
                      className="w-100"
                      name="model"
                      value={formValues.model || ""}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      id="outlined-basic"
                      label="Ano"
                      variant="outlined"
                      className="w-100"
                      name="year"
                      value={formValues.year || ""}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      id="outlined-basic"
                      label="Cor"
                      variant="outlined"
                      className="w-100"
                      name="color"
                      value={formValues.color || ""}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      id="outlined-basic"
                      label="Placa"
                      variant="outlined"
                      className="w-100"
                      name="plate"
                      value={formValues.plate || null}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      id="outlined-basic"
                      label="Chassi"
                      variant="outlined"
                      className="w-100"
                      name="chassis"
                      value={formValues.chassis || ""}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      id="outlined-basic"
                      label="Dados Adicionais"
                      variant="outlined"
                      className="w-100"
                      name="additional"
                      value={formValues.additional || ""}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      id="outlined-basic"
                      label="Identificação"
                      variant="outlined"
                      className="w-100"
                      name="cod"
                      value={formValues.cod || ""}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={12} className="text-center">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formValues.armored ?? false}
                          name="armored"
                          onChange={handleChange}
                        />
                      }
                      label="Veículo Blindado"
                    />
                  </Grid>

                  <Grid item md={3} xs={5}>
                    <InputMask
                      mask="99999-999"
                      value={formValues.deliveryCep || ""}
                      onChange={async (event) => {
                        const fieldValue = event.target.value;

                        setFormValues((prevValues) => ({
                          ...prevValues,
                          deliveryCep: fieldValue,
                        }));

                        if (fieldValue.replace(/\D/g, "").length === 8) {
                          await fetchCep(fieldValue.replace(/\D/g, ""));
                        }
                      }}
                      disabled={loadingCep}
                    >
                      {(inputProps) => (
                        <TextField
                          {...inputProps}
                          fullWidth
                          label="CEP"
                          disabled={loadingCep}
                        />
                      )}
                    </InputMask>
                  </Grid>

                  <Grid item lg={7} md={12} xs={7}>
                    <TextField
                      fullWidth
                      label="Rua"
                      name="deliveryStreet"
                      value={resultCep?.logradouro || ""}
                      InputProps={{
                        endAdornment: loadingCep && (
                          <CircularProgress size={20} />
                        ),
                      }}
                      disabled={loadingCep}
                      onChange={handleChange}
                    />
                  </Grid>

                  <Grid item lg={2} md={12} xs={5}>
                    <TextField
                      fullWidth
                      label="Número"
                      name="deliveryNumber"
                      value={formValues.deliveryNumber || ""}
                      inputProps={{ maxLength: 8 }}
                      InputProps={{
                        endAdornment: loadingCep && (
                          <CircularProgress size={20} />
                        ),
                      }}
                      disabled={loadingCep}
                      onChange={handleChange}
                    />
                  </Grid>

                  <Grid item lg={5} md={12} xs={7}>
                    <TextField
                      fullWidth
                      label="Bairro"
                      name="deliveryDistrict"
                      value={resultCep?.bairro || ""}
                      InputProps={{
                        endAdornment: loadingCep && (
                          <CircularProgress size={20} />
                        ),
                      }}
                      disabled={loadingCep}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item lg={5} md={12} xs={9}>
                    <TextField
                      fullWidth
                      label="Cidade"
                      name="deliveryCity"
                      value={resultCep?.localidade || ""}
                      InputProps={{
                        endAdornment: loadingCep && (
                          <CircularProgress size={20} />
                        ),
                      }}
                      disabled={loadingCep}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item lg={2} md={12} xs={3}>
                    <TextField
                      fullWidth
                      label="Estado"
                      name="deliveryState"
                      value={resultCep?.uf || ""}
                      inputProps={{ maxLength: 2 }}
                      InputProps={{
                        endAdornment: loadingCep && (
                          <CircularProgress size={20} />
                        ),
                      }}
                      disabled={loadingCep}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item lg={12} md={12} xs={12}>
                    <TextField
                      fullWidth
                      label="Complemento"
                      name="deliveryComplement"
                      value={formValues.deliveryComplement || ""}
                      InputProps={{
                        endAdornment: loadingCep && (
                          <CircularProgress size={20} />
                        ),
                      }}
                      disabled={loadingCep}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item lg={12} md={12} xs={12}>
                    <TextField
                      id="outlined-basic"
                      label="Observações"
                      variant="outlined"
                      className="w-100"
                      name="notes"
                      value={notes}
                      onChange={(e) => {
                        setNotes(e.target.value);
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Card>

            <CarCroqui
              setGetPositionsChecked={setGetPositionsChecked}
              useFunction
            />

            <Card sx={{ boxShadow: 3 }} className="mb-4">
              <Typography variant="h6">Documentação</Typography>
              <Box className="d-flex mb-1">
                <Grid
                  container
                  rowSpacing={1}
                  columnSpacing={{ xs: 12, sm: 12, md: 12 }}
                >
                  <Grid item xs={12} sm={12} md={12}>
                    <MuiFileInput
                      value={fileDocs.docVehicle || null}
                      onChange={(e) => handleInputChange("docVehicle", e)}
                      placeholder={"Documento do veículo"}
                      multiple
                    />
                  </Grid>
                </Grid>
              </Box>
            </Card>

            <Card sx={{ boxShadow: 3 }} className="mb-4">
              <Typography variant="h6">Fotos do veículo</Typography>
              <Box className="d-flex mb-1">
                <Grid
                  container
                  rowSpacing={1}
                  columnSpacing={{ xs: 12, sm: 12, md: 12 }}
                >
                  <Grid item xs={12} sm={12} md={12}>
                    <MuiFileInput
                      multiple
                      value={vehiclePics.vehiclePics || null}
                      onChange={(e) =>
                        handleInputChangeVehiclePic("vehiclePics", e)
                      }
                      placeholder={"Fotos do veículo"}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Card>

            <LoadingButton
              onClick={() =>
                handleSubmit({
                  formValues: {
                    ...formValues,
                    positionsChecked: getPositionsChecked,
                    notes,
                  },
                  fotos: { fileDocs, vehiclePics },
                  mode: "default",
                })
              }
              className="w-100 mb-1 mt-5"
              loading={isLoading}
              variant="outlined"
            >
              Finalizar
            </LoadingButton>
          </Scrollbar>
        </Card>

        <AlertSnackbar
          openAlert={showAlert}
          duration={2500}
          status={message.status}
          message={message.message}
          onClose={handleAlert}
        />
      </Container>
    </>
  );
}
